import useIsMobile from '@/hooks/useMobile'
import AppAssets from '@/styles/app_assets'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import Slider, { type CustomArrowProps, type Settings } from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import styled from 'styled-components'

const CarouselLayoutContainer = styled.div`
  .slick-track {
    display: flex;
    align-items: stretch;
    margin: 0;
  }
  // slick slide is the container of a single slide item. There is also a wrapper div before our custom component
  .slick-slide {
    display: flex;
    align-self: stretch;
    height: unset;
    > div {
      display: flex;
      align-self: stretch;
    }
  }
`

interface Props {
  children?: ReactNode
  settings?: Settings
  itemWidth: number
  itemGap?: number
  itemsLength: number
}

const HorizontalCarouselComponent = ({
  children,
  settings,
  itemWidth,
  itemGap = 0,
  itemsLength
}: Props): JSX.Element => {
  const isMobile = useIsMobile()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [widthSlider, setWidthSlider] = useState(itemWidth)

  const slideRef = useRef<any>(null)

  useEffect(() => {
    const offsetWidthElm =
      slideRef?.current?.innerSlider?.track?.node?.firstElementChild
        ?.offsetWidth ?? itemWidth
    if (isMobile) {
      setWidthSlider(offsetWidthElm)
    }
  }, [slideRef.current, window.innerWidth, isMobile])

  const slideShow = Math.floor(
    Math.min(window.innerWidth, 1080) / (widthSlider + itemGap)
  )

  const defaultSettings = useMemo(() => {
    const isHidenNextArrow =
      isMobile || itemsLength - currentIndex === slideShow
    const isHidenPrevArrow = isMobile || currentIndex === 0

    return {
      variableWidth: true,
      infinite: true,
      swipe: true,
      speed: 500,
      slidesToScroll: 1,
      swipeToSlide: true,
      nextArrow: isHidenNextArrow ? <></> : <NextArrow />,
      prevArrow: isHidenPrevArrow ? <></> : <PrevArrow />
    }
  }, [currentIndex])

  return (
    <CarouselLayoutContainer>
      <Slider
        ref={slideRef}
        {...defaultSettings}
        {...settings}
        slidesToShow={slideShow}
        afterChange={(currentSlide: number) => {
          setCurrentIndex(currentSlide)
        }}
      >
        {children}
      </Slider>
    </CarouselLayoutContainer>
  )
}

const NextArrow = (props: CustomArrowProps): JSX.Element => {
  const { className, style, onClick } = props
  return (
    <img
      src={AppAssets.icArrowNext}
      className={className}
      style={{
        ...style,
        display: 'block',
        zIndex: 2
      }}
      onClick={onClick}
    />
  )
}

const PrevArrow = (props: CustomArrowProps): JSX.Element => {
  const { className, style, onClick } = props
  return (
    <img
      src={AppAssets.icArrowPrev}
      className={className}
      style={{
        ...style,
        display: 'block',
        zIndex: 2
      }}
      onClick={onClick}
    />
  )
}

export default HorizontalCarouselComponent
